import { SelectionModel } from '@angular/cdk/collections';
import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, UntypedFormControl, Validators } from '@angular/forms';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { CalAngularService, ICvxClaimsPrincipal } from '@cvx/cal-angular';
import { PageLayout } from '@cvx/nextpage';
import { IGenericApiResponseWithWorkflowRequest } from 'src/app/models/common/GenericApiResponseWithWorkflowRequest';
import { IGraphUser } from 'src/app/models/graphUser';
import { IWorkflowRequest } from 'src/app/models/requests/WorkflowRequest';
import { SsSecret } from 'src/app/models/secretServer/SsSecret';
import { SecretServerService } from 'src/app/services/secretServer/secretServer.service';
import { DialogDirectorySearchComponent } from '../../_shared/dialog-directory-search/dialog-directory-search.component';
import { GroupMemberType } from 'src/app/models/directory/enums/GroupMemberType.enum';
import { DirectoryDomain } from 'src/app/models/directory/enums/DirectoryDomain.enum';
import { UserType } from 'src/app/models/directory/enums/UserType.enum';
import { IDialogDirectoryData } from 'src/app/models/components/IDialogDirectoryData';
import { MatDialog } from '@angular/material/dialog';
import { UpdateSecretPermissionsRequest } from 'src/app/models/secretServer/UpdateSecretPermissionsRequest';
import { Subject, Subscription, debounceTime, distinctUntilChanged, filter, finalize, lastValueFrom, switchMap, tap } from 'rxjs';
import { matTableSelectionHelper } from 'src/app/helpers/matTableSelectionHelper';
import { IPagedResponse } from 'src/app/models/common/PagedResponse';
import { GraphUserService } from 'src/app/services/graph/GraphUser.service';

@Component({
  selector: 'app-update-password-permissions',
  templateUrl: './update-password-permissions.component.html',
  styleUrls: ['./update-password-permissions.component.css']
})
export class UpdatePasswordPermissionsComponent implements OnInit, OnDestroy {
  @ViewChild("paginatorOwnedPassword", { static: false })
  set paginator(value: MatPaginator) {
    this.secretsDataSource.paginator = value;
  }
  @ViewChild("paginatorSupportUser", { static: false }) paginatorSupportUser: MatPaginator;

  justification = new UntypedFormControl('', { validators: [Validators.required] });
  currentPagedResponse: IPagedResponse<SsSecret> | null = null
  pageSizeOptions: number[] = [5, 10, 20, 30, 40];
  @ViewChild(MatSort, { static: false })
  set sort(value: MatSort) {
    if (this.usingAdminRole)
      this.allSecretsDataSource.sort = value;
    else
      this.secretsDataSource.sort = value;
  }

  addSelfErrorMessage = "you can't change your own permissions";
  ownersErrorMessage = "";
  viewersErrorMessage = "";
  revokeErrorMessage = "";

  PageLayout = PageLayout;
  currentUserProfile: ICvxClaimsPrincipal;
  gettingSecrets: boolean = true;
  isSubmitting: boolean = false;
  submittedRequests: IWorkflowRequest[] = [];
  submitErrorMessage = { message: '', errors: [] };
  selection = new SelectionModel<SsSecret>(true, []);
  retireResults: string[] = [];

  subscriptions: Subscription[] = []
  hasAdminRole: boolean = false;
  adminToggleOptions: string[];
  defaultRole: string = 'passwords owned';
  readonly adminRole: string = 'support';
  showAdminToggleOptions: boolean = false;
  usingAdminRole: boolean = false;
  searchTerm: string = "";
  searchTermChanged: Subject<string> = new Subject<string>();
  searchTermDebounceSeconds: number = 0.5;
  searchTermMinLength: number = 2;
  searchTermToolTip: string = `search requires a minumum of ${this.searchTermMinLength} characters`;

  secretsDataSource = new MatTableDataSource<SsSecret>([]);
  allSecretsDataSource = new MatTableDataSource<SsSecret>([]);
  secretsDisplayedColumns: string[] = ['select', 'name', 'folder'];
  selectedSecretsDisplayedColumns: string[] = ['name', 'folder'];

  observer = {
    next: (v: IPagedResponse<SsSecret>) => {
      this.currentPagedResponse = v;
      this.allSecretsDataSource.data = v.items?.filter(s => s.isShareable);
      this.allSecretsDataSource.paginator = this.paginatorSupportUser;
      this.allSecretsDataSource.sort = this.sort;
    },
    error: (e: any) => {
      console.error(e);
      this.gettingSecrets = false;
    },
    complete: () => this.gettingSecrets = false
  };

  constructor(private authService: CalAngularService,
    private dialog: MatDialog,
    private readonly userService: GraphUserService,
    private secretService: SecretServerService,
    private formBuilder: FormBuilder) { }

  passwordForm = this.formBuilder.group({
    folder: [null, Validators.required],
    name: ['', Validators.required],
    resource: ['', Validators.required],
    owners: this.formBuilder.array([], Validators.minLength(0)),
    viewers: this.formBuilder.array([], Validators.minLength(0)),
    revokeUsers: this.formBuilder.array([], Validators.minLength(0))
  });

  get owners() {
    let owners = this.passwordForm.get('owners') as FormArray
    return owners;
  }
  get viewers() { return this.passwordForm.get('viewers') as FormArray; }
  get revokeUsers() { return this.passwordForm.get('revokeUsers') as FormArray; }

  get passwordFormValues(): UpdateSecretPermissionsRequest {
    let owners = this.owners.value.map((item: IGraphUser) => item.id);
    let viewers = this.viewers.value.map((item: IGraphUser) => item.id);
    let revokeUsers = this.revokeUsers.value.map((item: IGraphUser) => item.id);

    let payload = new UpdateSecretPermissionsRequest(
      this.selection.selected.map(s => s.id),
      owners,
      viewers,
      revokeUsers,
      this.usingAdminRole,
      this.justification.value
    );

    return payload;
  }

  async ngOnInit(): Promise<void> {
    if (await lastValueFrom(this.authService.isUserSignedIn())) {
      this.currentUserProfile = this.authService.cvxClaimsPrincipal;
    }

    this.adminToggleOptions = [this.defaultRole, this.adminRole]

    // check if the user has Secret.Manage.All? if so show search all section on UI
    this.hasAdminRole = this.userService.CheckIsSecretManageAll(this.authService.cvxClaimsPrincipal);
    this.getUserOwnedPasswords();
  }

  handleToggleChange(selectedOption: string) {
    this.usingAdminRole = selectedOption === this.adminRole;
    this.selection = new SelectionModel<SsSecret>(true, []);
    this.owners.clear();
    this.revokeUsers.clear();
    this.viewers.clear();
    this.secretsDataSource = new MatTableDataSource<SsSecret>([]);
    this.allSecretsDataSource = new MatTableDataSource<SsSecret>([]);
    this.gettingSecrets = true;
    if (!this.usingAdminRole) {
      this.getUserOwnedPasswords();
    }
    else {
      this.searchTerm = "";
      this.setupSearch();
      this.pageChanged(new PageEvent);
    }
  }

  private getUserOwnedPasswords() {
    this.secretService.GetUserOwnedPasswords("").subscribe({
      next: (v) => {
        this.secretsDataSource.data = v.filter(s => s.isShareable);
        this.secretsDataSource.paginator = this.paginator;
        this.secretsDataSource.sort = this.sort;
      },
      error: (e) => {
        console.error(e);
        this.gettingSecrets = false;
      },
      complete: () => this.gettingSecrets = false
    });
  }

  setupSearch() {
    this.gettingSecrets = true;
    const sub = this.searchTermChanged.pipe(
      filter(res => {
        return res !== null &&
          (res.length >= this.searchTermMinLength)
      }),
      distinctUntilChanged(),
      debounceTime(1000 * this.searchTermDebounceSeconds),
      tap(() => {
        // minimum length required after initial load
        this.gettingSecrets = true;
      }),
      switchMap(value =>
        this.getAllPasswords(value as string)
          .pipe(
            finalize(() => {
              this.gettingSecrets = false
            })
          )
      )
    ).subscribe(this.observer);
    // setup for cleanup
    this.subscriptions.push(sub);
  }

  getAllPasswords(searchTerm: string) {
    this.gettingSecrets = true;
    const pageNumber = (this.paginatorSupportUser?.pageIndex ?? 0) + 1;
    const pageSize = (this.paginatorSupportUser?.pageSize ?? this.pageSizeOptions[0]);
    return this.secretService.GetRequestablePasswords(searchTerm, pageNumber, pageSize);
  }

  pageChanged(_: PageEvent) {
    const sub = this.getAllPasswords(this.searchTerm as string).subscribe(this.observer);
    // setup for cleanup
    this.subscriptions.push(sub);
  }

  selectPermission(row: SsSecret) {
    this.selection.toggle(row);
  }

  public doFilterAllPermissions = (event: any) => {
    const newSearchTerm: string = (event.target as HTMLInputElement)?.value;
    this.searchTermChanged.next(newSearchTerm)
  }
  public doFilterPermissions = (value: any) => {
    this.secretsDataSource.filter = value.target.value.trim().toLocaleLowerCase();
  }

  resetAddUserRoleErrorMessages() {
    this.ownersErrorMessage = "";
    this.viewersErrorMessage = "";
    this.revokeErrorMessage = "";
  }

  addUserByType(type: string, id: string, name: string, email: string) {
    this.resetAddUserRoleErrorMessages();
    let listOfUser = type == 'owners' ? this.owners : type == 'viewers' ? this.viewers : this.revokeUsers;

    const inOwners = this.owners.controls.findIndex(o => o.value.id == id) > -1;
    const inViewers = this.viewers.controls.findIndex(o => o.value.id == id) > -1;
    const inRevokeUsers = this.revokeUsers.controls.findIndex(o => o.value.id == id) > -1;
    const isCurrentUser = this.currentUserProfile.objectId == id;

    this.ownersErrorMessage = isCurrentUser && type == 'owners' ? this.addSelfErrorMessage : "";
    this.viewersErrorMessage = isCurrentUser && type == 'viewers' ? this.addSelfErrorMessage : "";
    this.revokeErrorMessage = isCurrentUser && type == 'revoke' ? this.addSelfErrorMessage : "";

    let isInList = inOwners || inViewers || inRevokeUsers || isCurrentUser;

    if (!isInList) {
      listOfUser.push(this.formBuilder.group({
        id: [id],
        displayName: [name],
        mail: [email]
      }));
    }
    else if (!isCurrentUser) {
      const errorMessage = `${name} can only have one permission change per request`;
      switch (type) {
        case 'owners':
          this.ownersErrorMessage = errorMessage
          break;
        case 'viewers':
          this.viewersErrorMessage = errorMessage
          break;
        case 'revoke':
          this.revokeErrorMessage = errorMessage
          break;
      }
    }
  }

  searchForPrimaryUser(type: string): void {
    const dialogRef = this.dialog.open(DialogDirectorySearchComponent, {
      disableClose: true,
      autoFocus: true,
      maxWidth: 1000,
      width: '100%',
      data: {
        type: GroupMemberType.User,
        domain: DirectoryDomain.Chevron,
        userType: UserType.Primary,
        filterGroupRemoveNotAvailableAsGroupMember: false,
        filterGroupRemoveDynamicMembershipEnabled: false,
        filterGroupOnlyManagedByIdamp: false
      } as IDialogDirectoryData
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        if (type == 'owners') this.addUserByType(type, result.id, result.displayName, result.mail);
        if (type == 'viewers') this.addUserByType(type, result.id, result.displayName, result.mail);
        if (type == 'revoke') this.addUserByType(type, result.id, result.displayName, result.mail);
      }
    });
  }

  removeOwner(i: number) {
    this.owners.removeAt(i);
  }
  removeViewer(i: number) {
    this.viewers.removeAt(i);
  }
  removeRevokeUsers(i: number) {
    this.revokeUsers.removeAt(i);
  }

  async onSubmit() {
    // clear error messages
    this.resetAddUserRoleErrorMessages();
    this.submitErrorMessage.message = '';
    this.submitErrorMessage.errors = [];

    if (this.selection.selected.length > 0 &&
      (this.owners.length > 0 || this.viewers.length > 0 || this.revokeUsers.length > 0)) {
      if (this.usingAdminRole && !this.justification.valid) {
        this.justification.markAsTouched({ onlySelf: true });
        return;
      }
      this.submitRequest();
    }
  }

  async submitRequest() {
    this.isSubmitting = true;
    let payload = this.passwordFormValues;

    const observer = {
      next: (x: IGenericApiResponseWithWorkflowRequest<SsSecret[]>) => {
        this.submittedRequests = x.requests;
      },
      error: (err: any) => {
        this.submitErrorMessage.message = err.statusText;
        this.submitErrorMessage.errors = err?.error?.errors ?? [];
        this.isSubmitting = false;
      },
      complete: () => {
        this.isSubmitting = false;
      }
    };

    let requestCall = this.secretService.UpdatePasswordPermissionsAsync(payload);
    requestCall.subscribe(observer);
  }

  //for checkbox select all use
  isAllSelected() {
    return matTableSelectionHelper.isAllSelected(this.selection, this.secretsDataSource);
  }
  masterToggle() {
    return matTableSelectionHelper.toggleSelectAll(this.selection, this.secretsDataSource);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe())
  }

}
